import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { EDBIntegratedOrders, OrderProduct } from "$types/EDBIntegratedOrders";
import { Reception } from "$types";
import ReferenceType from "$types/ReferenceType";
import { Services, SERVICES_URL } from "$utils/typeConstants";
import useAuth from "$hooks/useAuth";
import { getHeaders, normalizeArray } from "$utils/utils";

const { ORDER, PRODUCT } = ReferenceType;

const useRemainingIntegratedOrders = (
  integratedOrders: EDBIntegratedOrders,
  edbProducts: any[]
) => {
  const { token } = useAuth();

  // Get products that have a reception reference ID
  const relatedEdbProducts = useMemo(
    () => edbProducts.filter((p) => !!p.receptionReferenceId),
    [edbProducts]
  );

  // Get the IDs of the related reception references
  const relatedReceptionReferenceIds: number[] = useMemo(
    () => relatedEdbProducts.map((p) => p.receptionReferenceId),
    [relatedEdbProducts]
  );

  // Fetch receptions from the EDB-heavy endpoint, filtered by receptionReferenceId.
  // This avoids relying on the consolidated Strapi-like endpoint/populate and keeps other pages fast.
  const { data: receptionsResponse } = useQuery({
    queryKey: [
      "receptions",
      "edb",
      "byReceptionReferenceIds",
      relatedReceptionReferenceIds.join(","),
    ],

    queryFn: async () => {
      if (relatedReceptionReferenceIds.length === 0) return { data: [] };

      const gatewayUrl = `${
        SERVICES_URL[Services.GATEWAY_API]
      }/api/order/receptions/edb`;
      const params = new URLSearchParams();
      params.append(
        "receptionReferenceId",
        relatedReceptionReferenceIds.join(",")
      );
      // Keep defaults aligned with the backend endpoint
      params.append("page", "1");
      params.append("rowsPerPage", "100000");

      const url = `${gatewayUrl}?${params.toString()}`;
      const headers = getHeaders(token);

      const response = await fetch(url, { method: "GET", headers });
      if (!response.ok) {
        throw new Error(
          `Failed to fetch receptions (EDB): ${response.statusText}`
        );
      }
      return response.json();
    },

    enabled: relatedReceptionReferenceIds.length > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const receptions: Reception[] = useMemo(
    () => normalizeArray(receptionsResponse?.data || []),
    [receptionsResponse]
  );

  // Calculate the remaining integrated orders
  const remainingIntegratedOrders = useMemo(() => {
    if (!integratedOrders) return null;

    const substractProductQty = (
      res: OrderProduct[],
      qty: number,
      suborderId: number,
      productId: number
    ) => {
      const index = res?.findIndex(
        (io) =>
          io.suborderId === suborderId && io.suborderProductId === productId
      );
      if (res && index !== -1) {
        res[index].quantity -= qty;
      }
    };

    const res: OrderProduct[] = integratedOrders.orderProducts?.map((p) => ({
      ...p,
    })); // deep copy

    const normalizedReceptions = normalizeArray(receptions);
    const relatedReceptionReferences = normalizedReceptions
      .flatMap((r) =>
        normalizeArray(r.receptionReferences || []).map((rr) => ({
          ...rr,
          receptionId: r.id,
        }))
      )
      .filter((rr) => relatedReceptionReferenceIds.includes(rr.id));
    relatedReceptionReferences.forEach((rr) => {
      const {
        orders,
        referenceType,
        suborderReferences,
        receptionId,
        originalProductReference,
      } = rr;

      // ===== Product reference handling
      if (referenceType === PRODUCT) {
        // A product referencing has only one order and suborder
        const normalizedOrders = normalizeArray(orders || []);
        const suborder = normalizedOrders[0]?.suborders?.find(
          (s) => s.reference === suborderReferences[0]
        );
        const suborderProduct = suborder?.suborderProducts?.find(
          (sp) => sp.reference === originalProductReference
        );
        if (suborder && suborderProduct) {
          const quantity = relatedEdbProducts.find(
            (edbProduct) => edbProduct.receptionReferenceId === rr.id
          )?.qty;
          if (quantity) {
            substractProductQty(res, quantity, suborder.id, suborderProduct.id);
          }
        }
      }

      // ===== Order reference handling
      if (referenceType === ORDER) {
        /*
          For an order referencing: 
            - retrieve the related reception and productQuantity
            - for each productQuantity, find the suborderId and suborderProductId
            - substract the productQuantity receivedQuantity to the result
        */
        const reception = normalizedReceptions.find(
          (r) => r.id === receptionId
        );
        const productQuantity = normalizeArray(
          reception?.productQuantity || []
        );
        const filteredPQty = productQuantity.filter(
          (pq) =>
            suborderReferences.includes(pq.suborderReference) &&
            pq.receivedQuantity > 0
        );

        filteredPQty.forEach((pq) => {
          const normalizedOrders = normalizeArray(orders || []);
          const suborder = normalizedOrders
            .flatMap((o) => normalizeArray(o.suborders || []))
            .find((s) => s.reference === pq.suborderReference);
          const suborderProduct = suborder?.suborderProducts?.find(
            (sp) => sp.reference === pq.productId // productId is a product reference
          );
          if (suborder && suborderProduct) {
            substractProductQty(
              res,
              pq.receivedQuantity,
              suborder.id,
              suborderProduct.id
            );
          }
        });
      }
    });

    return {
      ...integratedOrders,
      orderProducts: res
        ?.filter((op) => op.quantity > 0)
        .sort((op1, op2) => {
          const orderSort = op1.order.reference.localeCompare(
            op2.order.reference
          );
          if (orderSort !== 0) return orderSort;

          // sort products by reference
          return (
            op1.order.suborders
              .find((s) => s.id === op1.suborderId)
              ?.suborderProducts.find((p) => p.id === op1.suborderProductId)
              ?.reference?.localeCompare(
                op2.order.suborders
                  .find((s) => s.id === op2.suborderId)
                  ?.suborderProducts.find((p) => p.id === op2.suborderProductId)
                  ?.reference
              ) || 0
          );
        }),
    };
  }, [
    integratedOrders,
    receptions,
    relatedEdbProducts,
    relatedReceptionReferenceIds,
  ]);

  return {
    remainingIntegratedOrders,
  };
};

export default useRemainingIntegratedOrders;
